/**
 * useOrganizerMutations Hooks
 *
 * Mutations for managing event organizers using URQL.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";
import type { EventOrganizer, OrganizerRole } from "../types.ts";

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const AddOrganizerDocument = `
  mutation AddEventOrganizer($eventId: ID!, $input: EventOrganizerInput!) {
    addEventOrganizer(eventId: $eventId, input: $input) {
      id
      ministry {
        id
        name
      }
      account {
        id
        firstName
        lastName
        username
      }
      role
      canEdit
      canManageRsvp
      canManageTickets
      receiveNotifications
    }
  }
`;

const RemoveOrganizerDocument = `
  mutation RemoveEventOrganizer($organizerId: ID!) {
    removeEventOrganizer(organizerId: $organizerId)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface OrganizerInput {
  ministryId?: string;
  accountId?: string;
  role: OrganizerRole;
  canEdit: boolean;
  canManageRsvp: boolean;
  canManageTickets: boolean;
  receiveNotifications: boolean;
}

interface UseAddOrganizerResult {
  addOrganizer: (eventId: string, input: OrganizerInput) => Promise<EventOrganizer>;
  isLoading: boolean;
  error: Error | null;
}

interface UseRemoveOrganizerResult {
  removeOrganizer: (organizerId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Add an organizer to an event.
 */
export function useAddOrganizer(): UseAddOrganizerResult {
  const [result, executeMutation] = useMutation<
    { addEventOrganizer: EventOrganizer },
    { eventId: string; input: OrganizerInput }
  >(AddOrganizerDocument);

  const addOrganizer = useCallback(
    async (eventId: string, input: OrganizerInput): Promise<EventOrganizer> => {
      try {
        const response = await executeMutation({ eventId, input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to add organizer");
          throw new Error(message);
        }

        if (!response.data?.addEventOrganizer) {
          throw new Error("No organizer returned");
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Organizer added",
          data: { eventId, organizerId: response.data.addEventOrganizer.id },
        });

        return response.data.addEventOrganizer;
      } catch (err) {
        const addError =
          err instanceof Error ? err : new Error("Failed to add organizer");

        errorTracking.captureError(addError, {
          tags: { feature: "admin", action: "addOrganizer" },
        });

        throw addError;
      }
    },
    [executeMutation]
  );

  return {
    addOrganizer,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Remove an organizer from an event.
 */
export function useRemoveOrganizer(): UseRemoveOrganizerResult {
  const [result, executeMutation] = useMutation<
    { removeEventOrganizer: boolean },
    { organizerId: string }
  >(RemoveOrganizerDocument);

  const removeOrganizer = useCallback(
    async (organizerId: string): Promise<void> => {
      try {
        const response = await executeMutation({ organizerId });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to remove organizer");
          throw new Error(message);
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Organizer removed",
          data: { organizerId },
        });
      } catch (err) {
        const removeError =
          err instanceof Error ? err : new Error("Failed to remove organizer");

        errorTracking.captureError(removeError, {
          tags: { feature: "admin", action: "removeOrganizer" },
        });

        throw removeError;
      }
    },
    [executeMutation]
  );

  return {
    removeOrganizer,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
